// @flow
import React, {Fragment} from 'react';
import classNames from 'classnames';
import {isNumber} from 'lodash';
import GeoDistance from '../GeoDistance/GeoDistance';
import {filter as filterGraphQlAnywhere} from 'graphql-anywhere';
import {DualPrice} from '../DualPrice/DualPrice';
import Rating, {fragments as ratingFragments} from '../Rating/Rating';
import Details from '../Details/Details';
import {FormattedMessage} from 'react-intl';
import RankedBadgesCollection from '../RankedBadgesCollection/RankedBadgesCollection';
import UrgencyMessage from '../UrgencyMessage/UrgencyMessage';
import BadgeTooltip from './ReviewBadgeTooltip';
import {FREE_CANCELLATION} from '../../constants/badges';
import WebRatingBadge from '../WebRatingBadge/WebRatingBadge';

import type {Node} from 'react';
import type {Props as HitInfoProps} from './HitInfo';
import type {BadgeShape as Badge} from '../Hit/Hit';

type Props = {
    isPriceAccurateAllowed: boolean
} & HitInfoProps;

export const hasBadges = (badges: Badge[]): boolean =>
    !!badges && !!badges.length;

/**
 * Classnames' flow-typed definition doesn't export an input type, so we're
 * using the deprecated existential type (*) to work around writing our own.
 */
type SuperlativeProps = {
    isDualPrice: boolean,
    superlativesBadges: Badge[]
};
const Superlative = ({isDualPrice, superlativesBadges}: SuperlativeProps) => {
    const superlativeClass = classNames({
        'HitInfo__superlative small': true,
        HitInfo__dualPriceSuperlative: isDualPrice
    });
    if (!superlativesBadges[0].helpText) {
        return (
            <span className={superlativeClass} aria-hidden="true">
                {superlativesBadges[0].name}
            </span>
        );
    }

    return (
        <span>
            <span className={superlativeClass} aria-hidden="true">
                {superlativesBadges[0].name}
            </span>
            <BadgeTooltip helpText={superlativesBadges[0].helpText} />
        </span>
    );
};

/**
 * Has two different content configurations based on the UK-only
 * `showIncludesTaxesAndFeesMessage` prop - see HitInfo#mapStateToProps for the
 * exact business logic.
 *
 * In the `showIncludesTaxesAndFeesMessage` configuration, a disclaimer is
 * displayed underneath the price, forcing the superlative to move over above
 * the rating stars, which forces the Premier Partner badge up into the badges
 * row with the other badges (free cancellation, rare find, etc).
 */
export default function HitInfoDesktop({
    brand,
    hitIndex,
    intl,
    isHovered,
    listing,
    isMetric,
    listingHasSecondaryPrice,
    isMultiUnitHit
}: Props) {
    let tooltipParentNode;
    const {propertyMetadata, geoDistance, unitMessage, unitMetadata} = listing;
    const rankedBadges = listing.rankedBadges || [];
    const superlativesBadges = listing.superlativesBadges || [];
    const webRatingBadges = listing.webRatingBadges || [];
    let freeCancellationBadge = rankedBadges.find(
        (e) => e.id === FREE_CANCELLATION
    );
    const headline = isMultiUnitHit
        ? (unitMetadata && unitMetadata.unitName) || ''
        : (propertyMetadata && propertyMetadata.headline) || '';
    /* istanbul ignore next */
    if (typeof window !== 'undefined') {
        tooltipParentNode = document.getElementsByTagName('body')[0];
    }
    let rateListing = filterGraphQlAnywhere(ratingFragments.Listing, listing);
    rateListing.starSize = listingHasSecondaryPrice ? '12' : '16';

    const SuperlativeBadges = ({isDualPrice}) => {
        if (!hasBadges(superlativesBadges)) {
            return null;
        }

        return (
            <Superlative
                isDualPrice={isDualPrice}
                superlativesBadges={superlativesBadges}
            />
        );
    };

    const DualPriceWrapper = () => {
        return (
            <footer className="media-flex__footer">
                <div className="HitInfo__badgeRatingGroup--old  HitInfo__badgeRatingGroup--left HitInfo__badgeRatingGroup--top">
                    <BadgeRatingGroup isDualPrice />
                </div>
                <div className="HitInfo__priceSuperlativeGroup--new HitInfo__badgeRatingGroup--right HitInfo__badgeRatingGroup--bottom">
                    <DualPrice listing={listing} intl={intl} isDesktop />
                </div>
            </footer>
        );
    };

    const RankedBadgesGroup = () => {
        return (
            <RankedBadgesCollection
                rankedBadges={rankedBadges}
                tooltipParentNode={tooltipParentNode}
                brand={brand}
                isDesktop
                isDualPrice
            />
        );
    };

    const BadgeRatingGroup = () => {
        const ratingClass = classNames({
            HitInfo__rating: true,
            HitInfo__dualPriceRating: true,
            'HitInfo__dualPriceRating--margin-top': !hasBadges(
                superlativesBadges
            )
        });

        const showWebRatingBadge =
            rateListing.reviewCount === 0 && hasBadges(webRatingBadges);

        return (
            <Fragment>
                <SuperlativeBadges isDualPrice />
                {showWebRatingBadge ? (
                    <WebRatingBadge badge={webRatingBadges[0]} renderTooltip />
                ) : (
                    <div className={ratingClass}>
                        <Rating listing={rateListing} isDualPrice />
                    </div>
                )}
            </Fragment>
        );
    };

    return (
        <Fragment>
            <div className="media-flex__body">
                <UrgencyMessage
                    unitMessage={unitMessage}
                    freeCancellationBadge={freeCancellationBadge}
                />
                <h2 className="HitInfo__headline hover-text" aria-hidden="true">
                    {headline}
                </h2>

                {isNumber(hitIndex) && (
                    <span className="sr-only">
                        <FormattedMessage
                            id="property"
                            description="Property"
                            defaultMessage="Property"
                        >
                            {(txt: Node): Node => (
                                <Fragment>
                                    {txt} {hitIndex + 1}
                                    {':'} {headline}
                                </Fragment>
                            )}
                        </FormattedMessage>
                    </span>
                )}

                <div className="HitInfo__details">
                    <Details
                        listing={listing}
                        isMetric={isMetric}
                        locale={intl.locale}
                    />
                </div>

                {geoDistance && (
                    <GeoDistance
                        brand={brand}
                        geoDistance={geoDistance}
                        isHighlighted={isHovered}
                    />
                )}
                <RankedBadgesGroup />
            </div>
            <DualPriceWrapper />
        </Fragment>
    );
}
